// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The building model's floor slabs and roof on the locked plan (ruxd-jmt):
 * which slabs a storey focus shows. Slabs come from `GET /slabs` as world
 * x/y footprints — an open outer ring plus holes — with the storey they
 * belong to and their base/top heights; `routes/bygning/PlanLayer.tsx`
 * projects them and draws them as faint fills under the spaces.
 *
 * Unlike `planSpaces`, nothing here ghosts the other storeys when there is
 * no focus: a slab carries no name to collide with, and a building's
 * stacked floor slabs mostly share one footprint, so drawing them all
 * superimposed reads as one floor plate. Pure: no React, no DOM.
 */

import type { SolidSlab } from '../api/types';

/**
 * The slabs to draw for storey `storeyId` (null = no storey focus): the
 * storey's own floor slabs and roof pieces, or every slab without a focus.
 * A slab on no stored storey (`storey_id` null) is shown only with no
 * focus; one whose footprint has fewer than three corners is never drawn.
 * Floor slabs come before roof pieces, so the roof is painted over them.
 */
export function planSlabs(slabs: readonly SolidSlab[] | null | undefined, storeyId: number | null): SolidSlab[] {
  const usable = (slabs ?? []).filter((s) => s.polygon.outer.length >= 3 && (storeyId === null || s.storey_id === storeyId));
  return [...usable.filter((s) => s.kind === 'floor'), ...usable.filter((s) => s.kind === 'ceiling'), ...usable.filter((s) => s.kind === 'roof')];
}

/**
 * How the plan draws a slab against its cut height `cutZ` (null: no cut):
 * a floor slab under the cut is seen below (a faint plate); a roof piece,
 * or any slab over the cut, is drawn as above the cut (a dashed outline).
 */
export function slabPlanStyle(slab: Pick<SolidSlab, 'kind' | 'z_base'>, cutZ: number | null): 'below' | 'above' {
  if (slab.kind !== 'floor') return 'above';
  return cutZ !== null && slab.z_base > cutZ ? 'above' : 'below';
}
